import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { createLogger } from '../../../../utils/logger';
const logger = createLogger('BreathingTubePreferencesInput');

import {
  FullBreathingTubeDetails,
  LimitedBreathingTubeDetails,
  SpecificMedicalSituationDetails,
  NaturalBreathingDetails,
  BreathingTubeDetails,
  breathingTubePreferencesService
} from '../../../../services/breathingTubePreferencesInfo';

type BreathingTubePreferencesInputScreenRouteProp = RouteProp<RootStackParamList, 'BreathingTubePreferencesInput'>;

type BreathingTubeType = 'full_breathing_tube' | 'limited' | 'specific' | 'natural' | null;
type LocationType = 'living_will' | 'living_trust' | 'advance_directive' | 'none' | null;

export const BreathingTubePreferencesInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<BreathingTubePreferencesInputScreenRouteProp>();

  const { breathingTubePreferencesData, isEditing } = route.params || {};

  const [selectedBreathingTube, setSelectedBreathingTube] = useState<BreathingTubeType>(
    breathingTubePreferencesData?.breathingTubeType as BreathingTubeType || null
  );
  const [selectedLocation, setSelectedLocation] = useState<LocationType>(
    breathingTubePreferencesData?.locationOfDirective as LocationType || null
  );
  const [specialInstructions, setSpecialInstructions] = useState(
    breathingTubePreferencesData?.specialInstructions || ''
  );
  const [saving, setSaving] = useState(false);

  // Initialize details with existing data if editing
  const getInitialDetails = () => {
    if (breathingTubePreferencesData?.breathingTubeDetails && breathingTubePreferencesData.breathingTubeType) {
      return breathingTubePreferencesData.breathingTubeDetails;
    }
    return null;
  };

  const initialDetails = getInitialDetails();

  // Full Breathing Tube Details
  const [fullBreathingTubeDetails, setFullBreathingTubeDetails] = useState<FullBreathingTubeDetails>({
    specificInterventionsToDecline: (initialDetails as FullBreathingTubeDetails)?.specificInterventionsToDecline || '',
    discussedWithHealthcareProvider: (initialDetails as FullBreathingTubeDetails)?.discussedWithHealthcareProvider || false,
    conditionsForBreathingTube: (initialDetails as FullBreathingTubeDetails)?.conditionsForBreathingTube || '',
    additionalInstructions: (initialDetails as FullBreathingTubeDetails)?.additionalInstructions || '',
  });

  // Limited Breathing Tube Details
  const [limitedDetails, setLimitedDetails] = useState<LimitedBreathingTubeDetails>({
    acceptableInterventions: (initialDetails as LimitedBreathingTubeDetails)?.acceptableInterventions || '',
    limitedBreathingTubeConditions: (initialDetails as LimitedBreathingTubeDetails)?.limitedBreathingTubeConditions || '',
    medicationPreferences: (initialDetails as LimitedBreathingTubeDetails)?.medicationPreferences || '',
    timeLimits: (initialDetails as LimitedBreathingTubeDetails)?.timeLimits || '',
  });

  // Specific Medical Situation Details
  const [specificDetails, setSpecificDetails] = useState<SpecificMedicalSituationDetails>({
    triggeringConditions: (initialDetails as SpecificMedicalSituationDetails)?.triggeringConditions || '',
    exceptions: (initialDetails as SpecificMedicalSituationDetails)?.exceptions || '',
    treatmentPreferences: (initialDetails as SpecificMedicalSituationDetails)?.treatmentPreferences || '',
    discussedWithDoctor: (initialDetails as SpecificMedicalSituationDetails)?.discussedWithDoctor || false,
  });

  // Natural Breathing Details
  const [naturalDetails, setNaturalDetails] = useState<NaturalBreathingDetails>({
    comfortCarePreferences: (initialDetails as NaturalBreathingDetails)?.comfortCarePreferences || '',
    painManagementOptions: (initialDetails as NaturalBreathingDetails)?.painManagementOptions || '',
    spiritualCulturalPractices: (initialDetails as NaturalBreathingDetails)?.spiritualCulturalPractices || '',
    environmentPreferences: (initialDetails as NaturalBreathingDetails)?.environmentPreferences || '',
  });

  const handleSave = async () => {
    // Validation - require at least breathing tube type selection
    if (!selectedBreathingTube) {
      Alert.alert('Error', 'Please select a breathing tube preference type');
      return;
    }

    try {
      setSaving(true);

      // Determine which breathing tube details to save based on selected type
      let breathingTubeDetails: BreathingTubeDetails = null;

      switch (selectedBreathingTube) {
        case 'full_breathing_tube':
          breathingTubeDetails = fullBreathingTubeDetails;
          break;
        case 'limited':
          breathingTubeDetails = limitedDetails;
          break;
        case 'specific':
          breathingTubeDetails = specificDetails;
          break;
        case 'natural':
          breathingTubeDetails = naturalDetails;
          break;
      }

      const breathingTubeUpdateData = {
        breathingTubeType: selectedBreathingTube || '',
        locationOfDirective: selectedLocation || '',
        specialInstructions: specialInstructions,
        breathingTubeDetails,
        notes: specialInstructions, // Map specialInstructions to notes field
      };

      let savedBreathingTubePreferences;

      if (isEditing && breathingTubePreferencesData?.id) {
        // Update existing record
        savedBreathingTubePreferences = await breathingTubePreferencesService.updateBreathingTubePreferences(
          breathingTubePreferencesData.id,
          breathingTubeUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedBreathingTubePreferences?.data || savedBreathingTubePreferences;
        logger.debug('Breathing tube preferences updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection
        navigation.navigate('BreathingTubePreferencesLockUnlock', { breathingTubePreferencesId: updatedData.id });
      } else {
        // Create new record
        savedBreathingTubePreferences = await breathingTubePreferencesService.createBreathingTubePreferences(breathingTubeUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedBreathingTubePreferences?.data || savedBreathingTubePreferences;
        logger.debug('Breathing tube preferences saved successfully with ID:', createdData.id);

        // Navigate to lock/unlock screen with the ID
        navigation.navigate('BreathingTubePreferencesLockUnlock', { breathingTubePreferencesId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving breathing tube preferences:', error);
      Alert.alert('Error', 'Failed to save breathing tube preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderBreathingTubeDetailsInput = () => {
    switch (selectedBreathingTube) {
      case 'full_breathing_tube':
        return (
          <View>
            <Text style={styles.detailsTitle}>Full Breathing Tube Decline Details:</Text>

            {/* Specific interventions to decline */}
            <Text style={styles.inputLabel}>What specific breathing assistance interventions do you want to decline?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe specific interventions to decline..."
              value={fullBreathingTubeDetails.specificInterventionsToDecline}
              onChangeText={(value) =>
                setFullBreathingTubeDetails({ ...fullBreathingTubeDetails, specificInterventionsToDecline: value })
              }
              multiline
            />

            {/* Conditions for breathing tube */}
            <Text style={styles.inputLabel}>Are there any specific conditions under which you would want a breathing tube?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe any exceptions or conditions..."
              value={fullBreathingTubeDetails.conditionsForBreathingTube}
              onChangeText={(value) =>
                setFullBreathingTubeDetails({ ...fullBreathingTubeDetails, conditionsForBreathingTube: value })
              }
              multiline
            />

            {/* Additional instructions */}
            <Text style={styles.inputLabel}>Any other specific instructions regarding breathing tube preferences?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Additional instructions..."
              value={fullBreathingTubeDetails.additionalInstructions}
              onChangeText={(value) =>
                setFullBreathingTubeDetails({ ...fullBreathingTubeDetails, additionalInstructions: value })
              }
              multiline
            />
          </View>
        );
      case 'limited':
        return (
          <View>
            <Text style={styles.detailsTitle}>Limited Breathing Tube Details:</Text>

            {/* Acceptable interventions */}
            <Text style={styles.inputLabel}>What specific breathing assistance interventions would you accept?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe acceptable interventions..."
              value={limitedDetails.acceptableInterventions}
              onChangeText={(value) =>
                setLimitedDetails({ ...limitedDetails, acceptableInterventions: value })
              }
              multiline
            />

            {/* Conditions for limited breathing tube */}
            <Text style={styles.inputLabel}>Under what conditions would you want limited breathing assistance?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe conditions for limited breathing assistance..."
              value={limitedDetails.limitedBreathingTubeConditions}
              onChangeText={(value) =>
                setLimitedDetails({ ...limitedDetails, limitedBreathingTubeConditions: value })
              }
              multiline
            />

            {/* Medication preferences */}
            <Text style={styles.inputLabel}>What are your preferences for medication during breathing assistance?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe medication preferences..."
              value={limitedDetails.medicationPreferences}
              onChangeText={(value) =>
                setLimitedDetails({ ...limitedDetails, medicationPreferences: value })
              }
              multiline
            />

            {/* Time limits */}
            <Text style={styles.inputLabel}>Any specific time limits for breathing tube use?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe time limits..."
              value={limitedDetails.timeLimits}
              onChangeText={(value) =>
                setLimitedDetails({ ...limitedDetails, timeLimits: value })
              }
              multiline
            />
          </View>
        );
      case 'specific':
        return (
          <View>
            <Text style={styles.detailsTitle}>Specific Medical Situations:</Text>

            {/* Triggering conditions */}
            <Text style={styles.inputLabel}>What specific medical conditions trigger your breathing tube preferences?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe triggering conditions..."
              value={specificDetails.triggeringConditions}
              onChangeText={(value) =>
                setSpecificDetails({ ...specificDetails, triggeringConditions: value })
              }
              multiline
            />

            {/* Exceptions */}
            <Text style={styles.inputLabel}>Are there any exceptions to your breathing tube preferences?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe any exceptions..."
              value={specificDetails.exceptions}
              onChangeText={(value) =>
                setSpecificDetails({ ...specificDetails, exceptions: value })
              }
              multiline
            />

            {/* Treatment preferences */}
            <Text style={styles.inputLabel}>What breathing treatments would you accept or decline?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe treatment preferences..."
              value={specificDetails.treatmentPreferences}
              onChangeText={(value) =>
                setSpecificDetails({ ...specificDetails, treatmentPreferences: value })
              }
              multiline
            />
          </View>
        );
      case 'natural':
        return (
          <View>
            <Text style={styles.detailsTitle}>Natural Breathing Preferences:</Text>

            {/* Comfort care preferences */}
            <Text style={styles.inputLabel}>What are your preferences for comfort care regarding breathing?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe comfort care preferences..."
              value={naturalDetails.comfortCarePreferences}
              onChangeText={(value) =>
                setNaturalDetails({ ...naturalDetails, comfortCarePreferences: value })
              }
              multiline
            />

            {/* Pain management options */}
            <Text style={styles.inputLabel}>What pain management options would you prefer for breathing difficulties?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe pain management preferences..."
              value={naturalDetails.painManagementOptions}
              onChangeText={(value) =>
                setNaturalDetails({ ...naturalDetails, painManagementOptions: value })
              }
              multiline
            />

            {/* Spiritual/cultural practices */}
            <Text style={styles.inputLabel}>Are there specific spiritual or cultural practices to consider regarding breathing?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe spiritual/cultural practices..."
              value={naturalDetails.spiritualCulturalPractices}
              onChangeText={(value) =>
                setNaturalDetails({ ...naturalDetails, spiritualCulturalPractices: value })
              }
              multiline
            />

            {/* Environment preferences */}
            <Text style={styles.inputLabel}>What environmental preferences do you have for breathing care?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe environmental preferences..."
              value={naturalDetails.environmentPreferences}
              onChangeText={(value) =>
                setNaturalDetails({ ...naturalDetails, environmentPreferences: value })
              }
              multiline
            />
          </View>
        );
      default:
        return null;
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Add Your Breathing Tube Preferences
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          <View style={styles.radioGroup}>
            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedBreathingTube('full_breathing_tube')}
            >
              <View style={styles.radioButton}>
                {selectedBreathingTube === 'full_breathing_tube' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Full Decline of Breathing Tube</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedBreathingTube('limited')}
            >
              <View style={styles.radioButton}>
                {selectedBreathingTube === 'limited' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Limited Breathing Assistance</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedBreathingTube('specific')}
            >
              <View style={styles.radioButton}>
                {selectedBreathingTube === 'specific' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Specific Medical Situations</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedBreathingTube('natural')}
            >
              <View style={styles.radioButton}>
                {selectedBreathingTube === 'natural' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Natural Breathing Preferences</Text>
            </TouchableOpacity>
          </View>

          {renderBreathingTubeDetailsInput()}

          <Text style={styles.label}>Breathing tube instructions:</Text>
          <TextInput
            style={styles.specialInstructionsInput}
            placeholder="Enter any special instructions or additional preferences regarding your breathing tube..."
            value={specialInstructions}
            onChangeText={setSpecialInstructions}
            multiline
          />

          <Text style={styles.sectionTitle}>
            Where is your Breathing Tube Preference located?
          </Text>

          <View style={styles.radioGroup}>
            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedLocation('living_will')}
            >
              <View style={styles.radioButton}>
                {selectedLocation === 'living_will' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>In your living Will</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedLocation('living_trust')}
            >
              <View style={styles.radioButton}>
                {selectedLocation === 'living_trust' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>In your living Trust</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedLocation('advance_directive')}
            >
              <View style={styles.radioButton}>
                {selectedLocation === 'advance_directive' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>In your advanced directive</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedLocation('none')}
            >
              <View style={styles.radioButton}>
                {selectedLocation === 'none' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>None of the above</Text>
            </TouchableOpacity>
          </View>

          <TouchableOpacity
            style={[styles.saveButton, saving && { opacity: 0.7 }]}
            onPress={handleSave}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Save'}</Text>
            )}
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  radioGroup: {
    marginBottom: ms(20),
  },
  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(20),
  },
  radioButton: {
    width: s(24),
    height: s(24),
    borderRadius: ms(12),
    borderWidth: ms(2),
    borderColor: '#1C2C4F',
    marginRight: ms(10),
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioButtonSelected: {
    width: s(12),
    height: s(12),
    borderRadius: ms(6),
    backgroundColor: '#1C2C4F',
  },
  radioText: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#F2994A',
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  specialInstructionsInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(30),
    minHeight: s(150),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  detailsTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(12),
    marginTop: ms(8),
  },
  inputLabel: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
    marginTop: ms(12),
  },
  structuredInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(12),
    fontSize: ms(14, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
    minHeight: s(80),
    textAlignVertical: 'top',
    marginBottom: ms(8),
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    marginBottom: ms(30),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
});